import { Component, computed, input, model } from '@angular/core';
import { UiChevronComponent, UiNumberComponent } from '@design-system';
import { calculateClosingPriceVarPercent, guid } from '@tools';
import { DailyHistory } from '../../../../models/daily-history.model';

/** One label/figure pair in the card's list. */
interface DailyHistoryRow {
	label: string;
	value: number;
}

/**
 * The list animates open and closed by collapsing a grid track rather than by animating
 * a `max-height`, so it eases to whatever height the rows actually take — at any font
 * size — instead of to a height guessed at in advance.
 */
const LIST_BASE_CLASSES =
	'grid w-full transition-[grid-template-rows,visibility] duration-300 ease-in-out motion-reduce:transition-none';
const LIST_OPEN_CLASSES = 'grid-rows-[1fr] visible';
const LIST_CLOSED_CLASSES = 'grid-rows-[0fr] invisible';

@Component({
	selector: 'app-daily-history-card',
	imports: [UiChevronComponent, UiNumberComponent],
	templateUrl: './daily-history-card.component.html',
	host: {
		class: 'block w-full'
	}
})
export class DailyHistoryCardComponent {
	readonly history = input.required<DailyHistory>();

	readonly previousClosePrice = input<number | null>(null);

	readonly expanded = model(false);

	private readonly id = guid();
	readonly headerId = `daily-history-card-${this.id}`;
	readonly listId = `daily-history-card-list-${this.id}`;

	/** The five figures the sheet lists, in the sheet's order — پایانی leads. */
	private readonly rows = computed<DailyHistoryRow[]>(() => {
		const history = this.history();

		return [
			{ label: 'پایانی', value: history.settlementPrice },
			{ label: 'بیشترین', value: history.maxPrice },
			{ label: 'آخرین', value: history.closePrice },
			{ label: 'اولین', value: history.openPrice },
			{ label: 'کمترین', value: history.minPrice }
		];
	});

	/** The row a closed card keeps; {@link extraRows} are the ones opening it reveals. */
	readonly primaryRow = computed(() => this.rows()[0]);

	readonly extraRows = computed(() => this.rows().slice(1));

	readonly changePercent = computed<number | null>(() => {
		const previousClosePrice = this.previousClosePrice();
		if (previousClosePrice === null) {
			return null;
		}

		const history = this.history();

		return calculateClosingPriceVarPercent(history.closePrice, previousClosePrice, history.tradedQuantity);
	});

	readonly listClasses = computed(
		() => `${LIST_BASE_CLASSES} ${this.expanded() ? LIST_OPEN_CLASSES : LIST_CLOSED_CLASSES}`
	);

	toggle(): void {
		this.expanded.update(value => !value);
	}
}
